/**
 * Which columns the grid shows.
 *
 * A plan carries far more per task than fits beside a chart — the outline number, the
 * constraint, the deadline, both kinds of slack, the hours, the money, what was agreed
 * at baseline. Rather than pick seven of them for everybody, the grid ships with the
 * seven most people want and lets anyone take the rest. Whatever is ticked is shown;
 * the grid scrolls sideways when there is more than fits, and the split between grid
 * and chart is yours to drag.
 */
import { Button } from '../../components/ui'
import { Tip } from './Toolbar'
import { ColumnsIcon } from '../../components/icons'
import { Menu, MenuDivider, MenuLabel } from '../../components/overlay'
import { COLUMNS } from './columns'
import type { MenuEntry } from './ContextMenu'

/**
 * The same choice as a right-click menu on the grid's header — the way every other
 * planning tool offers it, and the one place a person hunting for a column looks first.
 * Right-clicking a particular column also offers to hide that one.
 */
export function columnMenuEntries(
  choice: { keys: string[]; toggle: (key: string) => void; reset: () => void },
  key?: string,
): MenuEntry[] {
  const entries: MenuEntry[] = [{ kind: 'heading', label: 'Columns' }]
  const under = key ? COLUMNS.find((c) => c.key === key) : undefined
  if (under && !under.fixed) {
    entries.push(
      { kind: 'item', label: `Hide ${under.label}`, onSelect: () => choice.toggle(under.key) },
      { kind: 'divider' },
    )
  }
  for (const column of COLUMNS) {
    entries.push({
      kind: 'check',
      label: column.label,
      checked: column.fixed || choice.keys.includes(column.key),
      disabled: column.fixed,
      onToggle: () => choice.toggle(column.key),
    })
  }
  entries.push({ kind: 'divider' }, { kind: 'item', label: 'Back to the usual seven', onSelect: () => choice.reset() })
  return entries
}

export function ColumnChooser({
  choice,
}: {
  choice: { keys: string[]; toggle: (key: string) => void; reset: () => void }
}) {
  return (
    <Menu
      width={300}
      label="Columns"
      trigger={(props) => (
        <Tip label="Choose which columns the grid shows">
          <Button size="sm" {...props} aria-label="Columns">
            <ColumnsIcon />
          </Button>
        </Tip>
      )}
    >
      {() => (
        <>
          <MenuLabel>Show in the grid</MenuLabel>
          <div className="max-h-[60vh] overflow-y-auto">
            {COLUMNS.map((column) => {
              const on = column.fixed || choice.keys.includes(column.key)
              return (
                <label
                  key={column.key}
                  className="flex cursor-pointer items-start gap-2 px-3 py-1 text-[13px] hover:bg-[var(--color-surface-sunk)]"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={on}
                    disabled={column.fixed}
                    onChange={() => choice.toggle(column.key)}
                  />
                  <span className="min-w-0 flex-1">
                    {column.label}
                    {column.hint && (
                      <span className="block text-[11px] text-[var(--color-ink-faint)]">
                        {column.hint}
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
          <MenuDivider />
          <div className="px-3 py-1.5">
            <Button size="sm" onClick={choice.reset}>
              Back to the usual seven
            </Button>
          </div>
        </>
      )}
    </Menu>
  )
}
